import { useEffect } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Save, Loader2, Palette, Globe, Phone, DollarSign, Percent, Gift, Settings2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { TopBanner } from '@/components/TopBanner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';

export const Route = createFileRoute('/_authenticated/ajustes')({
  component: AjustesPage,
});

const settingsSchema = z.object({
  siteName: z.string().min(1, 'Requerido'),
  siteUrl: z.string().url('URL inválida'),
  timezone: z.string(),
  whatsappSupport: z.string().optional(),
  minWithdrawal: z.coerce.number().min(0),
  minDeposit: z.coerce.number().min(0),
  welcomeBonusPercent: z.coerce.number().min(0).max(500),
  welcomeBonusMinDeposit: z.coerce.number().min(0),
  referralPrize: z.coerce.number().min(0),
  operatingMode: z.enum(['manual', 'internal', 'lambda']),
  autoWithdrawalThreshold: z.coerce.number().min(0),
  maxWithdrawalsPerDay: z.coerce.number().min(0),
  themeColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Color hex inválido'),
});

type SettingsForm = z.infer<typeof settingsSchema>;

interface SettingsData {
  siteName: string;
  siteUrl: string;
  timezone: string;
  whatsappSupport?: string;
  minWithdrawal: number;
  minDeposit: number;
  welcomeBonusPercent: number;
  welcomeBonusMinDeposit: number;
  referralPrize: number;
  operatingMode: 'manual' | 'internal' | 'lambda';
  autoWithdrawalThreshold: number;
  maxWithdrawalsPerDay: number;
  themeColor: string;
}

function AjustesPage() {
  const qc = useQueryClient();

  const { data: settings, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: async () => {
      const { data } = await apiClient.get('/admin/settings');
      return data as SettingsData;
    },
  });

  const mutation = useMutation({
    mutationFn: async (body: SettingsForm) => {
      const { data } = await apiClient.put('/admin/settings', body);
      return data;
    },
    onSuccess: () => {
      toast.success('Ajustes guardados');
      qc.invalidateQueries({ queryKey: ['settings'] });
    },
    onError: () => toast.error('Error al guardar'),
  });

  const form = useForm<SettingsForm>({
    resolver: zodResolver(settingsSchema),
    defaultValues: {
      siteName: 'Clone Chat',
      siteUrl: 'https://localhost:5173',
      timezone: 'America/Argentina/Buenos_Aires',
      whatsappSupport: '',
      minWithdrawal: 1000,
      minDeposit: 500,
      welcomeBonusPercent: 200,
      welcomeBonusMinDeposit: 1000,
      referralPrize: 0,
      operatingMode: 'lambda',
      autoWithdrawalThreshold: 50000,
      maxWithdrawalsPerDay: 0,
      themeColor: '#d4a843',
    },
  });

  useEffect(() => {
    if (settings) {
      form.reset(settings);
    }
  }, [settings, form]);

  const onSubmit = (values: SettingsForm) => {
    mutation.mutate(values);
  };

  if (isLoading) {
    return (
      <>
        <TopBanner title="Ajustes" subtitle="Configuración del tenant" />
        <div className="flex-1 overflow-auto p-6 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </>
    );
  }

  return (
    <>
      <TopBanner title="Ajustes" subtitle="Configuración del tenant" />
      <div className="flex-1 overflow-auto p-6">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 max-w-4xl">
            {/* Personalización */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Palette className="h-5 w-5" />
                  Personalización
                </CardTitle>
                <CardDescription>Tema, colores y nombre del sitio</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="siteName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Nombre del sitio</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="themeColor"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Color principal</FormLabel>
                        <div className="flex gap-2">
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <input
                            type="color"
                            value={field.value}
                            onChange={(e) => field.onChange(e.target.value)}
                            className="h-10 w-10 rounded border p-1"
                          />
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Config General */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="h-5 w-5" />
                  Configuración General
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="siteUrl"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>URL del sitio</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="timezone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Zona horaria</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="America/Argentina/Buenos_Aires">Buenos Aires (GMT-3)</SelectItem>
                            <SelectItem value="America/Mexico_City">Ciudad de México (GMT-6)</SelectItem>
                            <SelectItem value="America/Bogota">Bogotá (GMT-5)</SelectItem>
                            <SelectItem value="America/Santiago">Santiago (GMT-4)</SelectItem>
                            <SelectItem value="Europe/Madrid">Madrid (GMT+1)</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="whatsappSupport"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>WhatsApp Soporte</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="+5491112345678" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Límites y Bonos */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  Límites y Bonos
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-3">
                  <FormField
                    control={form.control}
                    name="minWithdrawal"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Mínimo retiro ($)</FormLabel>
                        <FormControl>
                          <Input type="number" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="minDeposit"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Mínimo depósito ($)</FormLabel>
                        <FormControl>
                          <Input type="number" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="welcomeBonusPercent"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-1">
                          <Gift className="h-3.5 w-3.5" />
                          Bono bienvenida (%)
                        </FormLabel>
                        <FormControl>
                          <Input type="number" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="welcomeBonusMinDeposit"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Depósito mínimo para bono ($)</FormLabel>
                        <FormControl>
                          <Input type="number" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="referralPrize"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Premio por referido ($)</FormLabel>
                        <FormControl>
                          <Input type="number" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Modo Operativo */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings2 className="h-5 w-5" />
                  Modo Operativo
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="operatingMode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Modo de retiro</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="manual">Manual (siempre requiere aprobación)</SelectItem>
                            <SelectItem value="internal">Interno (procesa internamente)</SelectItem>
                            <SelectItem value="lambda">Lambda (automático con wallet)</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormDescription>
                          Lambda = retiro automático vía wallet (requiere history)
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="autoWithdrawalThreshold"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Umbral manual ($)</FormLabel>
                        <FormControl>
                          <Input type="number" {...field} />
                        </FormControl>
                        <FormDescription>
                          Retiros ≥ este monto requieren aprobación manual
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="maxWithdrawalsPerDay"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Retiros por día (0 = ilimitado)</FormLabel>
                        <FormControl>
                          <Input type="number" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-end">
              <Button type="submit" disabled={mutation.isPending} size="lg">
                {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                <Save className="mr-2 h-4 w-4" />
                Guardar ajustes
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </>
  );
}
